import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Moon, Sun, Calculator, MessageSquare, FileText, Menu, LogOut } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';

/**
 * TopNavigationMobile Component
 * 
 * Mobile version with top bar and bottom navigation.
 * Features:
 * - Top bar: Hamburger menu, brand, theme toggle, profile
 * - Bottom nav: Three tabs (Taxper, Chat, Contly) with iOS-style active indicator
 */
const TopNavigationMobile = ({
  activeView = 'chat',
  onViewChange,
  theme = 'dark',
  onThemeToggle,
  onProfileClick,
  onRightSidebarToggle,
  onSidebarToggle
}) => {
  const navigate = useNavigate();
  const { currentUser, userData, signOut } = useAuth();

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleBrandClick = () => {
    navigate('/app');
  };

  const handleProfileClick = () => {
    if (onProfileClick) {
      onProfileClick();
    } else {
      navigate('/profile');
    }
  };

  const tabs = [
    { id: 'taxper', label: 'Taxper', icon: Calculator },
    { id: 'chat', label: 'Chat', icon: MessageSquare },
    { id: 'contly', label: 'Contly', icon: FileText },
  ];

  return (
    <>
      {/* Top Bar - MOBILE: Fixed height with safe area */}
      <header
        className="fixed top-0 left-0 right-0 z-[100] overflow-hidden md:hidden"
        style={{
          height: 'calc(48px + env(safe-area-inset-top, 0px))',
          paddingTop: 'env(safe-area-inset-top, 0px)',
          // Light theme: Same translucent/glass treatment as dark, but with light colors
          background: theme === 'dark' ? 'rgba(0, 0, 0, 0.5)' : 'rgba(255, 255, 255, 0.5)',
          backdropFilter: 'blur(24px)',
          WebkitBackdropFilter: 'blur(24px)',
          // Light theme: Very light gray border (same opacity structure as dark)
          borderBottom: theme === 'dark'
            ? '1px solid rgba(255, 255, 255, 0.08)'
            : '1px solid rgba(0, 0, 0, 0.08)',
          // Light theme: Soft/low-contrast shadows (same structure as dark)
          boxShadow: theme === 'dark'
            ? '0 18px 40px rgba(0, 0, 0, 0.5)'
            : '0 18px 40px rgba(0, 0, 0, 0.1)',
        }}
        data-theme={theme}
      >
        <div className="h-12 flex items-center justify-between px-3">
          {/* Left: Hamburger Menu */}
          <div className="flex items-center gap-2">
            {onSidebarToggle && (
              <button
                className="p-1.5 rounded-full cursor-pointer transition-transform duration-200 active:scale-95"
                onClick={onSidebarToggle}
                aria-label="Menu"
                style={{
                  minWidth: '36px',
                  minHeight: '36px',
                  // Light theme: Same structure as dark, but with light colors
                  background: theme === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
                  border: theme === 'dark' ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)',
                  // Light theme: Neutral dark-gray tone (no blue highlight)
                  color: theme === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Menu size={18} />
              </button>
            )}

            {/* Brand */}
            <div
              className="text-[14px] font-bold leading-none cursor-pointer lawnance-brand"
              style={{
                // Light theme: Dark text (same weight/size/spacing as dark mode)
                color: theme === 'dark' ? '#ffffff' : '#000000',
                letterSpacing: '0.030em',
              }}
              onClick={handleBrandClick}
            >
              LAWNANCE
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-1.5">
            {/* Theme Toggle */}
            <button
              className="p-1.5 rounded-full cursor-pointer transition-transform duration-200 active:scale-[0.98]"
              onClick={onThemeToggle}
              aria-label="Toggle theme"
              style={{
                minWidth: '36px',
                minHeight: '36px',
                // Light theme: Same structure as dark, but with light colors
                background: theme === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
                border: theme === 'dark' ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)',
                // Light theme: Neutral dark-gray tone (no blue ring highlight)
                color: theme === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {theme === 'dark' ? <Moon size={16} /> : <Sun size={16} />}
            </button>

            {/* Welcome Text - Mobile */}
            {currentUser && (
              <div
                className="text-xs font-medium px-2 py-1 rounded-full max-w-[120px] truncate"
                style={{
                  background: theme === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
                  border: theme === 'dark' ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)',
                  color: theme === 'dark' ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.9)',
                }}
              >
                {userData?.fullName || currentUser.displayName || 'User'}
              </div>
            )}

            {/* Logout */}
            <button
              className="p-1.5 rounded-full cursor-pointer transition-transform duration-200 active:scale-[0.98]"
              onClick={handleLogout}
              aria-label="Logout"
              style={{
                minWidth: '36px',
                minHeight: '36px',
                background: theme === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
                border: theme === 'dark' ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)',
                color: theme === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </header>

      {/* Bottom Navigation Bar - MOBILE: Fixed height with safe area */}
      <nav
        className="fixed bottom-0 left-0 right-0 z-[100] md:hidden"
        style={{
          height: 'calc(64px + env(safe-area-inset-bottom, 0px))',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
          background: theme === 'dark' ? 'rgba(0, 0, 0, 0.5)' : 'rgba(248, 249, 250, 0.8)',
          backdropFilter: 'blur(24px)',
          WebkitBackdropFilter: 'blur(24px)',
          borderTop: theme === 'dark'
            ? '1px solid rgba(255, 255, 255, 0.08)'
            : '1px solid rgba(148, 163, 184, 0.6)',
          boxShadow: theme === 'dark'
            ? '0 -18px 40px rgba(0, 0, 0, 0.5)'
            : '0 -8px 32px 0 rgba(15, 23, 42, 0.1), 0 -2px 8px 0 rgba(0, 0, 0, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.8)',
        }}
        data-theme={theme}
      >
        <div className="h-16 flex items-center justify-around px-2">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeView === tab.id;

            return (
              <button
                key={tab.id}
                onClick={() => onViewChange?.(tab.id)}
                className="flex flex-col items-center justify-center gap-1 px-4 py-2 rounded-full transition-transform duration-200 active:scale-95 relative touch-manipulation"
                style={{
                  color: isActive
                    ? (theme === 'dark' ? '#ffffff' : '#0f172a')
                    : (theme === 'dark' ? 'rgba(255, 255, 255, 0.5)' : '#64748b'),
                  background: isActive
                    ? (theme === 'dark' ? 'rgba(255, 255, 255, 0.15)' : 'rgba(249, 250, 251, 0.85)')
                    : 'transparent',
                  minWidth: '60px',
                  minHeight: '48px', // MOBILE: Minimum touch target
                }}
              >
                <Icon size={20} />
                <span className="text-[11px] font-medium">{tab.label}</span>
                {/* MOBILE: Larger active indicator */}
                {isActive && (
                  <div
                    className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 w-8 h-1 rounded-full"
                    style={{
                      background: theme === 'dark' ? '#3b82f6' : '#3b82f6',
                      boxShadow: '0 0 8px rgba(59, 130, 246, 0.6)',
                    }}
                  />
                )}
              </button>
            );
          })}
        </div>
      </nav>
    </>
  );
};

export default TopNavigationMobile;

